import { ListItemIcon, ListItemText, MenuItem, Typography } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import AppContext, { FAVORITES_URL_PARAM_FOLDER, OBJECT_TYPE_FAVORITE } from '../../context/AppContext';
import FavoritesManager, { FAVORITE_FILE_TYPE, getSize } from '../../manager/FavoritesManager';
import styles from '../trackfavmenu.module.css';
import ActionsMenu from '../actions/ActionsMenu';
import { ReactComponent as FolderIcon } from '../../assets/icons/ic_action_folder.svg';
import { ReactComponent as FolderHiddenIcon } from '../../assets/icons/ic_action_folder_hidden.svg';
import { ReactComponent as FolderPinIcon } from '../../assets/icons/ic_action_folder_pin.svg';
import FavoriteGroupActions from '../actions/FavoriteGroupActions';
import MenuItemWithLines from '../components/MenuItemWithLines';
import { useTranslation } from 'react-i18next';
import FileShareIcon from '../share/FileShareIcon.jsx';
import { getShare } from '../../manager/track/TracksManager';
import DividerWithMargin from '../../frame/components/dividers/DividerWithMargin';
import ThreeDotsButton from '../../frame/components/btns/ThreeDotsButton';
import { SHARE_TYPE } from '../share/shareConstants';
import { fmt } from '../../util/dateFmt';
import { useNavigate } from 'react-router-dom';
import { createUrlParams } from '../../util/Utils';
import { FAVORITES_URL, liveHash, MAIN_URL_WITH_SLASH } from '../../manager/GlobalManager';
import { useZoomToFit } from '../../util/hooks/map/useZoomToFit';
import { useProcessDownload } from '../../util/hooks/menu/useProcessDownload';

export default function FavoriteGroup({ index, group, smartf = null, showDivider = true }) {
    const ctx = useContext(AppContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { zoomToFit } = useZoomToFit();
    const [processDownload, setProcessDownload] = useProcessDownload(FAVORITE_FILE_TYPE, smartf?.type, group.name);

    const [openActions, setOpenActions] = useState(false);
    const anchorEl = useRef(null);
    const share = getShare(group.file, ctx);
    const groupSize = getSize(group, t);

    const sharedFile = smartf?.type === SHARE_TYPE;

    const iconColor = FavoritesManager.getColorGroup({ favoritesGroup: group, groupName: group.name });
    const iconStyle = { fill: group.name && iconColor };

    useEffect(() => {
        if (ctx.favorites.mapObjs?.[group.id]?.markers && group.name === ctx.selectedGpxFile.file?.name) {
            const updatedFile = {
                ...ctx.selectedGpxFile.file,
                markers: ctx.favorites.mapObjs[group.id].markers,
            };
            ctx.setSelectedGpxFile({
                ...ctx.selectedGpxFile,
                file: updatedFile,
            });
        }
    }, [ctx.favorites]);

    return (
        <>
            <MenuItem
                className={styles.group}
                key={'group' + group.id + index}
                id={'se-menu-fav-' + group.name}
                onClick={(e) => {
                    if (e.target !== 'path') {
                        // an open point card keeps the favorites sub-route, so the folder is opened by its own address
                        navigate(
                            {
                                pathname: MAIN_URL_WITH_SLASH + FAVORITES_URL,
                                search: createUrlParams({ [FAVORITES_URL_PARAM_FOLDER]: group.name }),
                                hash: liveHash(),
                            },
                            { preventScrollReset: true }
                        );
                        zoomToFit({ type: OBJECT_TYPE_FAVORITE, object: group });
                    }
                }}
            >
                <ListItemIcon className={styles.icon}>
                    {group.hidden === 'true' ? (
                        <FolderHiddenIcon id={'se-fav-menu-icon-hidden-' + group.name} />
                    ) : group.pinned ? (
                        <FolderPinIcon style={iconStyle} />
                    ) : (
                        <FolderIcon
                            style={iconStyle}
                            id={`se-fav-group-icon-${group.name}-${iconColor?.replace(/^#/, '')}`}
                        />
                    )}
                </ListItemIcon>
                <ListItemText id={`se-fav-group-size-${groupSize}`}>
                    <MenuItemWithLines name={group.name} maxLines={2} />
                    <Typography variant="body2" component="div" className={styles.groupInfo} noWrap>
                        {share && !sharedFile && <FileShareIcon />}
                        {`${fmt.monthShortDay(Number(group.clienttimems))}, ${groupSize}`}
                    </Typography>
                </ListItemText>
                <ThreeDotsButton
                    name={'action_menu_group'}
                    tip={'shared_string_menu'}
                    id={`se-folder-actions-button-${group.name}`}
                    setOpenActions={setOpenActions}
                    anchorEl={anchorEl}
                    processDownload={processDownload}
                />
            </MenuItem>
            {showDivider && <DividerWithMargin margin={'64px'} />}
            <ActionsMenu
                open={openActions}
                setOpen={setOpenActions}
                anchorEl={anchorEl}
                actions={
                    <FavoriteGroupActions
                        group={group}
                        setOpenActions={setOpenActions}
                        setProcessDownload={setProcessDownload}
                        smartf={smartf}
                    />
                }
            />
        </>
    );
}
